import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { signIn, signOut, fetchAuthSession, getCurrentUser, confirmSignIn } from 'aws-amplify/auth';

export interface AuthUser {
  email: string;
  sub: string;
  groups: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject = new BehaviorSubject<AuthUser | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(true);

  user$ = this.userSubject.asObservable();
  isLoading$ = this.loadingSubject.asObservable();

  get user(): AuthUser | null {
    return this.userSubject.value;
  }

  get isAuthenticated(): boolean {
    return this.userSubject.value !== null;
  }

  get isAdmin(): boolean {
    return this.userSubject.value?.groups.includes('Admin') ?? false;
  }

  get isSuperAdmin(): boolean {
    return this.userSubject.value?.groups.includes('SuperAdmin') ?? false;
  }

  constructor() {
    this.checkAuthStatus();
  }

  async checkAuthStatus(): Promise<void> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (session.tokens) {
        const currentUser = await getCurrentUser();
        const idToken = session.tokens.idToken;
        const groups = (idToken?.payload?.['cognito:groups'] as string[]) || [];
        this.userSubject.next({
          email: currentUser.signInDetails?.loginId || '',
          sub: currentUser.userId,
          groups,
        });
      }
    } catch {
      this.userSubject.next(null);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  async login(email: string, password: string): Promise<{ requiresPasswordChange: boolean }> {
    const result = await signIn({ username: email, password });

    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { requiresPasswordChange: true };
    }

    if (result.isSignedIn) {
      await this.checkAuthStatus();
    }

    return { requiresPasswordChange: false };
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    await confirmSignIn({ challengeResponse: newPassword });
    await this.checkAuthStatus();
  }

  async logout(): Promise<void> {
    await signOut();
    this.userSubject.next(null);
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }
}
